// src/pages/TenantSetup.jsx
import React, { useState } from 'react';
import { FiPlay, FiCheck, FiAlertCircle, FiHome } from 'react-icons/fi';
import { runTenantSetup } from '../utils/runTenantSetup';
import { assignUserToTenant } from '../utils/setupTenants';
import { debugTenantSetup } from '../utils/debugTenantSetup';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const TenantSetup = () => {
  const [setupStatus, setSetupStatus] = useState('idle'); // idle, running, success, error
  const [setupResults, setSetupResults] = useState(null);
  const [error, setError] = useState(null);
  const [debugStatus, setDebugStatus] = useState('idle');
  const { currentUser } = useAuth();

  const handleRunSetup = async () => {
    try {
      setSetupStatus('running');
      setError(null);
      
      console.log('🚀 Starting tenant setup...');
      
      // First run debug to check if everything is working
      console.log('🔍 Running debug checks first...');
      const debugResult = await debugTenantSetup();
      
      if (!debugResult.success) {
        throw new Error(`Debug check failed: ${debugResult.error}`);
      }
      
      console.log('✅ Debug checks passed, proceeding with setup...');
      const results = await runTenantSetup();
      
      setSetupResults(results);
      setSetupStatus('success');
      toast.success('Multi-tenant setup completed successfully!');
      
    } catch (err) {
      console.error('Setup failed:', err);
      setError(err.message);
      setSetupStatus('error');
      toast.error('Setup failed: ' + err.message);
    }
  };

  const handleAssignToTenant = async (tenantId, role = 'admin') => {
    if (!currentUser?.uid) {
      toast.error('No user logged in');
      return;
    }

    try {
      await assignUserToTenant(currentUser.uid, tenantId, role);
      toast.success('Successfully assigned to tenant!');
    } catch (err) {
      console.error('Assignment failed:', err);
      toast.error('Failed to assign user to tenant');
    }
  };

  const handleDebugTest = async () => {
    try {
      setDebugStatus('running');
      setError(null);
      
      console.log('🔍 Running debug test...');
      const debugResult = await debugTenantSetup();
      
      if (debugResult.success) {
        setDebugStatus('success');
        toast.success('Debug test passed! Firebase connection is working.');
      } else {
        setDebugStatus('error');
        setError(debugResult.error);
        toast.error('Debug test failed: ' + debugResult.error);
      }
      
    } catch (err) {
      console.error('Debug test failed:', err);
      setError(err.message);
      setDebugStatus('error');
      toast.error('Debug test failed: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <FiHome className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            HealeHub Multi-Tenant Setup
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Initialize your multi-tenant HealeHub system with sample clinic tenants.
          </p>
        </div>

        {/* Setup Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Initialize Tenants</h2>
              <p className="text-gray-600">Create the initial clinic configurations</p>
            </div>
            
            {setupStatus === 'idle' && (
              <div className="flex gap-3">
                <button
                  onClick={handleDebugTest}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2 text-sm"
                  disabled={debugStatus === 'running'}
                >
                  {debugStatus === 'running' ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  ) : (
                    <FiAlertCircle className="h-3 w-3" />
                  )}
                  Test Firebase
                </button>
                <button
                  onClick={handleRunSetup}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium"
                >
                  <FiPlay className="h-4 w-4" />
                  Run Setup
                </button>
              </div>
            )}
            
            {setupStatus === 'running' && (
              <div className="flex items-center gap-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Setting up...</span>
              </div>
            )}
            
            {setupStatus === 'success' && (
              <div className="flex items-center gap-2 text-green-600">
                <FiCheck className="h-4 w-4" />
                <span>Setup Complete</span>
              </div>
            )}
            
            {setupStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-600">
                <FiAlertCircle className="h-4 w-4" />
                <span>Setup Failed</span>
              </div>
            )}
          </div>

          {/* Setup Steps */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                {setupStatus === 'success' ? (
                  <FiCheck className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Create Tenant Records</h3>
                <p className="text-sm text-gray-600">Initialize "The Rogue Method" and "Heale Rehab" in Firebase</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                {setupStatus === 'success' ? (
                  <FiCheck className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Configure Settings</h3>
                <p className="text-sm text-gray-600">Set up business hours, treatment types, and rooms for each clinic</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                {setupStatus === 'success' ? (
                  <FiCheck className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Initialize Collections</h3>
                <p className="text-sm text-gray-600">Create tenant-scoped data collections in Firebase</p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-900">Setup Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Display */}
        {setupResults && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Setup Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {setupResults.map((tenant, index) => (
                <div key={tenant.id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                      <p className="text-sm text-gray-600">{tenant.description}</p>
                    </div>
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tenant.branding?.primaryColor || '#2563eb' }}
                    />
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tenant ID:</span>
                      <span className="font-mono text-xs">{tenant.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Slug:</span>
                      <span>/{tenant.slug}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span>{tenant.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Treatments:</span>
                      <span>{tenant.settings?.treatmentTypes?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rooms:</span>
                      <span>{tenant.settings?.rooms?.length || 0}</span>
                    </div>
                  </div>

                  {currentUser && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handleAssignToTenant(tenant.id, 'admin')}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
                      >
                        Assign Me as Admin
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next Steps */}
        {setupStatus === 'success' && (
          <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="font-medium text-green-900 mb-3">🎉 Setup Complete! Next Steps:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-green-800">
              <li>Assign yourself to a tenant using the "Assign Me as Admin" buttons above</li>
              <li>Navigate to Admin → Tenant Management to manage your clinics</li>
              <li>Update your existing components to use the new multi-tenant data hooks</li>
              <li>Test switching between clinics using the tenant switcher in the sidebar</li>
              <li>Migrate your existing data to the appropriate tenant using the migration tools</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantSetup;
